import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { exchangePhotoPairing, PhoneGrant, SESSION_PHOTO_LIMIT, uploadSessionPhoto } from '../services/api/sessionPhotoService';

const MAX_PHOTO_BYTES = 20 * 1024 * 1024;

type Item = { id: string; file: File; preview: string; progress: number; status: 'ready' | 'uploading' | 'done' | 'error'; error?: string; previewUnavailable?: boolean };

export function MobilePhotoUploadPage({ pairingToken }: { pairingToken: string }) {
  const [grant, setGrant] = useState<PhoneGrant | null>(null);
  const [error, setError] = useState('');
  const [items, setItems] = useState<Item[]>([]);
  const [uploadingAll, setUploadingAll] = useState(false);
  const [limitNotice, setLimitNotice] = useState('');
  const batchInFlight = useRef(false);
  const inFlight = useRef(new Set<string>());
  const pairing = useRef<Promise<PhoneGrant> | null>(null);
  const previews = useRef<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!pairingToken) { setError('Scan a new QR code from the patient visit. Refreshing this page ends your pairing.'); return; }
    pairing.current ||= exchangePhotoPairing(pairingToken);
    pairing.current.then(result => { if (!cancelled) setGrant(result); })
      .catch(reason => { if (!cancelled) setError(reason.message); });
    return () => { cancelled = true; };
  }, [pairingToken]);
  useEffect(() => () => { previews.current.forEach(url => URL.revokeObjectURL(url)); }, []);

  const photoLimit = grant?.photo_limit ?? SESSION_PHOTO_LIMIT;
  const alreadyInVisit = grant?.photo_count ?? 0;
  const uploadedHere = items.filter(item => item.status === 'done').length;
  const uploadableSelected = items.filter(item => item.file.size <= MAX_PHOTO_BYTES).length;
  const slotsLeft = Math.max(0, photoLimit - alreadyInVisit - uploadableSelected);
  const visitFull = alreadyInVisit + uploadedHere >= photoLimit;
  const add = (files: FileList | null) => {
    if (!files) return;
    const chosen = Array.from(files);
    let room = slotsLeft;
    const accepted = chosen.filter(file => {
      if (file.size > MAX_PHOTO_BYTES) return true;
      if (room <= 0) return false;
      room -= 1;
      return true;
    });
    const skipped = chosen.length - accepted.length;
    setLimitNotice(!skipped ? ''
      : slotsLeft === 0 ? `This visit already has ${photoLimit} photos, so no more can be added.`
      : `Only ${slotsLeft} more ${slotsLeft === 1 ? 'photo' : 'photos'} can be added to this visit, so ${skipped} ${skipped === 1 ? 'was' : 'were'} left out.`);
    const additions = accepted.map(file => {
      const preview = URL.createObjectURL(file); previews.current.push(preview);
      return { id: crypto.randomUUID(), file, preview, progress: 0, status: 'ready' as const };
    });
    setItems(current => [...current, ...additions]);
  };
  const send = async (item: Item) => {
    if (!grant || inFlight.current.has(item.id) || item.status === 'done') return;
    const update = (changes: Partial<Item>) => setItems(current => current.map(value => value.id === item.id ? { ...value, ...changes } : value));
    if (item.file.size > MAX_PHOTO_BYTES) {
      update({ status: 'error', error: 'Choose a photo smaller than 20 MiB.' });
      return;
    }
    inFlight.current.add(item.id);
    update({ status: 'uploading', error: undefined });
    try {
      await uploadSessionPhoto(grant, item.file, item.id, progress => update({ progress }));
      update({ status: 'done', progress: 100 });
    } catch (reason) { update({ status: 'error', error: reason instanceof Error ? reason.message : 'Upload failed.' }); }
    finally { inFlight.current.delete(item.id); }
  };
  const pendingItems = items.filter(item => item.status === 'ready' || item.status === 'error');
  const sendAll = async () => {
    if (batchInFlight.current || inFlight.current.size) return;
    batchInFlight.current = true;
    setUploadingAll(true);
    try {
      // Keep camera-sized requests sequential; each photo retains its own retry ID.
      for (const item of pendingItems) await send(item);
    } finally {
      batchInFlight.current = false;
      setUploadingAll(false);
    }
  };
  return <Box className="mp-block mp-no-track" sx={{ maxWidth: 560, mx: 'auto', p: 3 }}>
    <Typography variant="h4" gutterBottom>Upload visit photos</Typography>
    {error && <Alert severity="warning">{error}</Alert>}
    {!grant && !error && <Typography>Connecting to your visit…</Typography>}
    {grant && <>
      <Typography>Visit {grant.session_id}{grant.visit_date ? ` · ${grant.visit_date}` : ''}</Typography>
      <Typography sx={{ my: 2 }}>Check the visit number against your desktop before uploading. This pairing expires at {new Date(grant.expires_at).toLocaleTimeString()}.</Typography>
      <Alert severity={visitFull ? 'warning' : 'info'} sx={{ mb: 2 }}>
        {visitFull
          ? `This visit has reached the ${photoLimit}-photo limit.`
          : `Up to ${photoLimit} photos per visit · ${alreadyInVisit + uploadedHere} uploaded · ${slotsLeft} more can be added`}
      </Alert>
      {limitNotice && <Alert severity="warning" sx={{ mb: 2 }}>{limitNotice}</Alert>}
      <Stack direction="row" spacing={2}>
        <Button component="label" variant="contained" disabled={uploadingAll || slotsLeft === 0}>Take photo<input aria-label="Take photo" disabled={uploadingAll || slotsLeft === 0} hidden type="file" accept="image/*" capture="environment" onChange={event => { add(event.target.files); event.target.value = ''; }} /></Button>
        <Button component="label" variant="outlined" disabled={uploadingAll || slotsLeft === 0}>Photo library<input aria-label="Photo library" disabled={uploadingAll || slotsLeft === 0} hidden type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" onChange={event => { add(event.target.files); event.target.value = ''; }} /></Button>
      </Stack>
      <Typography variant="body2" sx={{ mt: 1 }}>JPEG, PNG, WebP and HEIC · 20 MiB each · Up to {photoLimit} photos per visit</Typography>
      {items.length > 0 && <Box sx={{ mt: 2 }}>
        <Typography variant="body2" role="status" sx={{ mb: 1 }}>{items.filter(item => item.status === 'done').length} of {items.length} photos uploaded</Typography>
        {items.length > 1 && (pendingItems.length > 0 || uploadingAll) && <Button fullWidth variant="contained" onClick={sendAll}
          disabled={uploadingAll || items.some(item => item.status === 'uploading')}>
          {uploadingAll ? 'Uploading photos…' : 'Upload all photos'}
        </Button>}
      </Box>}
      <Stack spacing={2} sx={{ mt: 3 }}>{items.map((item, index) => <Box key={item.id}>
        {item.previewUnavailable ? <Typography color="text.secondary">Preview will be available on desktop after upload.</Typography>
          : <img src={item.preview} alt={`Selected upload ${index + 1}`} onError={() => setItems(current => current.map(value => value.id === item.id ? { ...value, previewUnavailable: true } : value))} style={{ maxWidth: '100%', maxHeight: 240 }} />}
        <Typography>Photo {index + 1}{item.status === 'done' ? ' — Uploaded to visit' : ''}</Typography>
        {item.status === 'uploading' && <LinearProgress variant="determinate" value={item.progress} />}
        {item.error && <Alert severity="error">{item.error}</Alert>}
        {items.length === 1 && ['ready', 'error'].includes(item.status) && <Button disabled={uploadingAll} onClick={() => send(item)}>{item.status === 'error' ? 'Retry photo' : 'Upload photo'}</Button>}
      </Box>)}</Stack>
    </>}
  </Box>;
}
